/**
 * Battle HUD: a name plate, spin gauge, and burst meter for each bey, plus a
 * big center status line (countdown / "LET IT RIP!").
 */
import Phaser from 'phaser';
import type { Bey } from '../entities/Bey.ts';
import { SIM } from '../game/config.ts';
import { specialFor } from '../game/specials.ts';
import { WORLD } from '../world.ts';

interface Plate {
  nameText: Phaser.GameObjects.Text;
  x: number;
  align: 'left' | 'right';
  color: number;
}

export class Hud {
  private readonly scene: Phaser.Scene;
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly status: Phaser.GameObjects.Text;
  private player!: Plate;
  private opponent!: Plate;
  private dashRdy!: Phaser.GameObjects.Text;
  private extRdy!: Phaser.GameObjects.Text;
  private specialRdy!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.g = scene.add.graphics().setDepth(50);
    this.status = scene.add
      .text(WORLD.width / 2, 150, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '54px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 8,
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(60);
  }

  setBeys(player: Bey, opponent: Bey): void {
    this.player = this.makePlate(player, 28, 'left');
    this.opponent = this.makePlate(opponent, WORLD.width - 28, 'right');

    const mk = (x: number, label: string) =>
      this.scene.add
        .text(x, 110, label, {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '14px',
          fontStyle: 'bold',
          color: '#3a4660',
        })
        .setDepth(60);
    this.dashRdy = mk(28, '● STRIKE');
    this.extRdy = mk(112, '● EXTREME');
    // The player's special pip names the move so you know what E does.
    this.specialRdy = mk(214, `● ${specialFor(player.def.type).name.toUpperCase()}`);

    // Name the opponent's special too (informational, right-aligned).
    this.scene.add
      .text(WORLD.width - 28, 110, `${specialFor(opponent.def.type).name.toUpperCase()} ◆`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#56607a',
      })
      .setOrigin(1, 0)
      .setDepth(60);

    this.scoreText = this.scene.add
      .text(WORLD.width / 2, 20, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5, 0)
      .setDepth(60);
  }

  /** Show the match score as pips: filled = rounds won, of `needed` to win. */
  setScore(playerWins: number, oppWins: number, needed: number): void {
    const pips = (n: number) => '●'.repeat(n) + '○'.repeat(Math.max(0, needed - n));
    this.scoreText.setText(`${pips(playerWins)}   vs   ${pips(oppWins)}`);
  }

  private makePlate(bey: Bey, x: number, align: 'left' | 'right'): Plate {
    const spin = bey.def.spinDir === 'left' ? ' ↺' : ' ↻';
    const nameText = this.scene.add
      .text(x, 24, bey.def.name.toUpperCase() + spin, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '26px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(align === 'left' ? 0 : 1, 0)
      .setDepth(60);
    return { nameText, x, align, color: bey.def.color };
  }

  setStatus(text: string, color = '#ffffff'): void {
    this.status.setText(text).setColor(color);
    this.status.setScale(text ? 1 : 0);
  }

  /** Briefly announce a fired special move's name on the appropriate side. */
  flashSpecial(isPlayer: boolean, name: string): void {
    const t = this.scene.add
      .text(isPlayer ? 260 : WORLD.width - 260, 220, name.toUpperCase() + '!', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        fontStyle: 'bold',
        color: '#5ad6ff',
        stroke: '#001018',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(62);
    this.scene.tweens.add({
      targets: t,
      y: 180,
      alpha: { from: 1, to: 0 },
      duration: 900,
      onComplete: () => t.destroy(),
    });
  }

  update(player: Bey, opponent: Bey): void {
    this.g.clear();
    this.drawBars(this.player, player);
    this.drawBars(this.opponent, opponent);

    const snap = player.snapshot();
    this.dashRdy.setColor(snap.dashCooldown <= 0 ? '#7CFC9A' : '#3a4660');
    this.extRdy.setColor(snap.extremeCooldown <= 0 ? '#FFD23B' : '#3a4660');
    this.specialRdy.setColor(player.specialReady ? '#5ad6ff' : '#3a4660');
  }

  private drawBars(plate: Plate, bey: Bey): void {
    const w = 320;
    const h = 20;
    const x = plate.align === 'left' ? plate.x : plate.x - w;
    const yGauge = 58;
    const yBurst = 84;
    const ySpecial = 95;

    // Spin gauge.
    const spinPct = Math.max(0, bey.spin) / SIM.MAX_SPIN;
    this.bar(x, yGauge, w, h, spinPct, plate.color, plate.align);

    // Burst meter (thinner, red as it fills).
    const burstPct = bey.burst / SIM.BURST.METER_MAX;
    const burstColor = burstPct > 0.66 ? 0xff2a2a : burstPct > 0.33 ? 0xff8a2a : 0xffd23b;
    this.bar(x, yBurst, w, 7, burstPct, burstColor, plate.align);

    // Special charge meter (cyan; bright when full).
    const sp = bey.snapshot();
    const specialPct = sp.special / sp.specialMax;
    this.bar(x, ySpecial, w, 7, specialPct, specialPct >= 1 ? 0x5ad6ff : 0x2a6f8a, plate.align);
  }

  private bar(
    x: number,
    y: number,
    w: number,
    h: number,
    pct: number,
    color: number,
    align: 'left' | 'right',
  ): void {
    // Backing.
    this.g.fillStyle(0x000000, 0.55);
    this.g.fillRoundedRect(x - 2, y - 2, w + 4, h + 4, 4);
    this.g.fillStyle(0x222a38, 1);
    this.g.fillRect(x, y, w, h);
    // Fill, anchored to the player's side.
    const fw = Math.max(0, Math.min(1, pct)) * w;
    this.g.fillStyle(color, 1);
    if (align === 'left') this.g.fillRect(x, y, fw, h);
    else this.g.fillRect(x + w - fw, y, fw, h);
    // Outline.
    this.g.lineStyle(2, 0xffffff, 0.25);
    this.g.strokeRect(x, y, w, h);
  }
}
